import {
  Button,
  ControlGroup,
  FileUpload,
  Group,
  Groups,
  IControlChangeEventArgs,
  Input,
  Text,
  Toggle,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { downloadBlob } from '@/utils/file-download'
import { generateId } from '@/utils/id-generator'

import {
  buildTemplateCsv,
  MAX_ROWS,
  OPTIONAL_COLUMN_DEFAULTS,
  REQUIRED_COLUMNS,
  TEMPLATE_FILENAME,
} from './bulk-import-template'

export interface BulkImportFormValues {
  /** Only populated by the staff variant, which shows the Account ID field. */
  accountId: string
  file: File | null
  suppressInvite: boolean
}

export interface BulkImportFormProps {
  /** Show the Account ID input (staff, who have no account of their own). */
  showAccountIdField?: boolean
  /** True while the upload is in flight. */
  isSubmitting: boolean
  onSubmit: (values: BulkImportFormValues) => void
  onCancel: () => void
  /**
   * Called when either input that decides what would be submitted changes — the
   * file or, on the staff surface, the account. The parent uses it to drop a rejection
   * that described the previous submission.
   */
  onInputChange?: () => void
  /** Optional accessible name for the <form> element. */
  formAriaLabel?: string
}

interface FieldError {
  key: string
  params?: Record<string, unknown>
}

type FormErrors = Partial<Record<'accountId' | 'file', FieldError>>

/**
 * The form a bulk import is started from, shared by both surfaces that offer one:
 * the account admin's Import tab on `/user/create` and the staff Import tab on
 * `/admin/user/create`. Only the account differs — an admin imports into their own,
 * staff name one — so it is a prop rather than a second form, following the same rule
 * that keeps the three Create User surfaces from drifting (KPS-448).
 *
 * The screen states what the file must look like *before* the admin prepares one, and
 * offers a template. What it deliberately does not state is any field length or
 * accepted value: those are the backend's and arrive in its rejection messages. This
 * epic already has one incident of a limit drifting because it lived in two places.
 */
export const BulkImportForm = ({
  showAccountIdField = false,
  isSubmitting,
  onSubmit,
  onCancel,
  onInputChange,
  formAriaLabel,
}: BulkImportFormProps): React.ReactElement => {
  const { t } = useTranslation()

  const [values, setValues] = React.useState<BulkImportFormValues>({
    accountId: '',
    file: null,
    suppressInvite: false,
  })
  const [errors, setErrors] = React.useState<FormErrors>({})

  const setAccountId = (e: IControlChangeEventArgs) => {
    // Retyping the account makes the previous outcome describe a different account. A
    // problem list, or a link to a run that already exists, left sitting beside a new
    // account id is read as being about that account.
    onInputChange?.()
    setErrors(prev =>
      prev.accountId ? { ...prev, accountId: undefined } : prev,
    )
    setValues(prev => ({ ...prev, accountId: e.currentValue }))
  }

  const handleFilesChosen = (files: FileList) => {
    const chosen = files.item(0)
    if (!chosen) return
    onInputChange?.()
    setErrors(prev => (prev.file ? { ...prev, file: undefined } : prev))
    setValues(prev => ({ ...prev, file: chosen }))
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()

    const next: FormErrors = {}
    if (showAccountIdField) {
      // Digits-only and positive, matching UserCreateForm: a number input still admits
      // "1.5", and parseInt("1.5") === 1 would import into the wrong account.
      const raw = values.accountId.trim()
      if (!/^\d+$/.test(raw) || parseInt(raw, 10) <= 0) {
        next.accountId = { key: 'ACCOUNT_ID_REQUIRED' }
      }
    }
    if (!values.file) {
      next.file = { key: 'BULK_IMPORT_FILE_REQUIRED' }
    }

    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }
    setErrors({})
    onSubmit(values)
  }

  const errorText = (field: keyof FormErrors): string | undefined => {
    const err = errors[field]
    return err ? t(err.key, err.params) : undefined
  }

  return (
    <form onSubmit={handleSubmit} aria-label={formAriaLabel}>
      <Groups>
        <Group tag='div'>
          <Text tag='p'>{t('BULK_IMPORT_INTRO')}</Text>
          <Text tag='p' dataTestingId='bulk-import-required-columns'>
            {`${t('BULK_IMPORT_REQUIRED_COLUMNS')} ${REQUIRED_COLUMNS.join(', ')}`}
          </Text>
          <Text tag='p'>{t('BULK_IMPORT_OPTIONAL_COLUMNS')}</Text>
          <ul data-testid='bulk-import-optional-columns'>
            {OPTIONAL_COLUMN_DEFAULTS.map(([column, defaultLabel]) => (
              <li key={column}>
                <Text tag='span'>
                  {`${column} — ${t('BULK_IMPORT_DEFAULTS_TO')} ${t(defaultLabel)}`}
                </Text>
              </li>
            ))}
          </ul>
          <Text tag='p' dataTestingId='bulk-import-row-limit'>
            {`${t('BULK_IMPORT_ROW_LIMIT')} ${MAX_ROWS}`}
          </Text>
          <Button
            label={t('BULK_IMPORT_DOWNLOAD_TEMPLATE')}
            theme='flat'
            onClick={() => downloadBlob(buildTemplateCsv(), TEMPLATE_FILENAME)}
            dataTestingId='bulk-import-download-template'
          />
        </Group>

        <Group tag='div'>
          {showAccountIdField && (
            <ControlGroup
              label={t('ACCOUNT_ID')}
              helpText={errorText('accountId')}
              control={
                <Input
                  type='number'
                  dataTestingId={generateId('input', 'bulk-import-account-id')}
                  dataTrackingId={generateId('input', 'bulk-import-account-id')}
                  handleChange={setAccountId}
                  id={generateId('input', 'bulk-import-account-id')}
                  value={values.accountId}
                  required
                  hasError={!!errors.accountId}
                />
              }
            />
          )}

          <ControlGroup
            label={t('BULK_IMPORT_FILE')}
            helpText={errorText('file')}
            control={
              <FileUpload
                id={generateId('input', 'bulk-import-file')}
                dataTestingId={generateId('input', 'bulk-import-file')}
                accept='.csv,text/csv'
                onFilesChosen={handleFilesChosen}
                label={t('BULK_IMPORT_CHOOSE_FILE')}
              >
                {t('BULK_IMPORT_CHOOSE_FILE')}
              </FileUpload>
            }
          />
          {values.file && (
            <Text tag='p' dataTestingId='bulk-import-chosen-file'>
              {values.file.name}
            </Text>
          )}

          <Toggle
            appearance='switch'
            type='checkbox'
            dataTestingId='bulk-import-suppress-invite'
            label={t('BULK_IMPORT_SUPPRESS_INVITE')}
            checked={values.suppressInvite}
            onChange={e =>
              setValues(prev => ({ ...prev, suppressInvite: e.currentValue }))
            }
          />
          {/* Stated rather than implied: the people are created either way, and the
              only difference is whether they hear about it now. */}
          <Text tag='p' size='sm'>
            {t('BULK_IMPORT_SUPPRESS_INVITE_HELP')}
          </Text>
        </Group>

        <Group tag='div'>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              label={t('CANCEL')}
              onClick={onCancel}
              theme='flat'
              disabled={isSubmitting}
            />
            <Button
              label={t(
                isSubmitting ? 'BULK_IMPORT_UPLOADING' : 'BULK_IMPORT_UPLOAD',
              )}
              type='submit'
              theme='primary'
              loading={isSubmitting}
            />
          </div>
        </Group>
      </Groups>
    </form>
  )
}
